import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Stack, alpha } from "@mui/material";
import { Restaurant as RestaurantIcon } from "@mui/icons-material";

import SelectableListRow from "../../molecules/SelectableListRow";
import ActionButton from "../../atoms/ActionButton";
import DietEditor from "./DietEditor";
import {
  LIST_ROW_SX,
  WORKSPACE_ROOT_SX,
  WorkspaceBody,
  WorkspaceSplit,
  WorkspaceSkeleton,
  WorkspaceEmpty,
  SelectPrompt,
  ListGroupLabel,
  ReadOnlyStrip,
} from "./_workspaceParts";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import { useHasPermission } from "../../../hooks/useHasPermission";
import {
  readWorkspaceState,
  writeWorkspaceState,
} from "../../../pages/Examination/ExaminationSession/workspaceStorage";
import {
  useListDietPlansByPatientMutation,
  useCreateDietPlanMutation,
  useSetDietPlanStatusMutation,
  useListPatientMeasurementsMutation,
} from "../../../store/secretaryApi";
import { PLAN_STATUS } from "../DietPlan/dietPlanFormat";
import useTerms from "../../../hooks/useTerms";

// @react-pdf + fontlar ayrı chunk (bkz. TreatmentEditor)
const DietPlanPreview = lazy(() => import("../pdf/DietPlanPreview"));

const TAB_KEY = "diet";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_) {
    return "—";
  }
};

const todayYmd = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Ölçüm listesinden kilo serisi: [{ takenAt, weight }] */
export const weightSeriesOf = (list) =>
  (list || [])
    .filter((m) => m.metricType === "weight")
    .map((m) => ({ takenAt: m.takenAt, weight: (m.values || []).find((v) => v.key === "weight")?.value }))
    .filter((p) => p.weight != null);

/**
 * DietTab — beslenme planları (DietPlan, tedaviden bağımsız; 2026-10-05).
 *
 * Sol: Aktif plan + geçmiş planlar. Sağ: DietEditor. "Yeni plan" planı hemen
 * oluşturur; aktif tedavide açık diyet talebi varsa plan o tedaviye bağlanır
 * (talep kapanır, eski mobil sürümler diyeti tedaviden okumaya devam eder).
 *
 * @property {"standalone" | "session"} mode
 */
export default function DietTab({
  mode,
  patientId,
  patient,
  token,
  canEdit = true,
  initialData,
  activeTreatment,
  onRecordChanged,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const canCreate = useHasPermission("diet:create") && canEdit;
  const canUpdate = useHasPermission("diet:update") && canEdit;
  const canReadMeasurements = useHasPermission("measurements:read");

  // ── Local alert ─────────────────────────────────────────────────
  const [localAlert, setLocalAlert] = useState({ open: false, severity: "success", message: "" });
  const alertTimerRef = useRef(null);
  const showLocal = useCallback((message, severity = "success", duration = 3500) => {
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    setLocalAlert({ open: true, severity, message });
    alertTimerRef.current = setTimeout(() => {
      setLocalAlert((a) => ({ ...a, open: false }));
      alertTimerRef.current = null;
    }, duration);
  }, []);
  const hideLocal = useCallback(() => setLocalAlert((a) => ({ ...a, open: false })), []);
  useEffect(() => () => alertTimerRef.current && clearTimeout(alertTimerRef.current), []);
  const showSuccess = useCallback((m) => showLocal(m, "success"), [showLocal]);
  const showError = useCallback((m) => showLocal(m, "error", 5000), [showLocal]);

  const [listPlans, { isLoading }] = useListDietPlansByPatientMutation();
  const [createPlan, { isLoading: creating }] = useCreateDietPlanMutation();
  const [setPlanStatus] = useSetDietPlanStatusMutation();
  const [listMeasurements] = useListPatientMeasurementsMutation();

  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [weightSeries, setWeightSeries] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [printPlan, setPrintPlan] = useState(null);

  const examinationId = initialData?.examinationId;

  const refresh = useCallback(async () => {
    if (!token || !patientId) return;
    try {
      const res = await listPlans({ token, patientId }).unwrap();
      setPlans(res?.data?.plans || []);
    } catch (_) {
      showError("Beslenme planları yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, patientId, listPlans, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!token || !patientId || !canReadMeasurements) return;
    listMeasurements({ token, patientId })
      .unwrap()
      .then((res) => {
        setMeasurements(res?.data?.list || []);
        setWeightSeries(weightSeriesOf(res?.data?.list));
      })
      .catch(() => setWeightSeries([]));
  }, [token, patientId, canReadMeasurements, listMeasurements]);

  const activePlan = useMemo(() => plans.find((p) => p.status === "active") || null, [plans]);
  const pastPlans = useMemo(() => plans.filter((p) => p.status !== "active"), [plans]);

  // ── Selection ───────────────────────────────────────────────────
  useEffect(() => {
    if (!loaded || plans.length === 0) return;
    if (mode === "session" && examinationId) {
      const restored = readWorkspaceState(examinationId)?.openRecords?.[TAB_KEY];
      if (restored && plans.some((r) => r._id === restored)) {
        setSelectedId(restored);
        return;
      }
    }
    setSelectedId((cur) => (cur && plans.some((p) => p._id === cur) ? cur : (activePlan || plans[0])._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, examinationId, loaded, plans.length]);

  useEffect(() => {
    if (mode !== "session" || !examinationId) return;
    writeWorkspaceState(examinationId, (prev) => ({
      ...prev,
      openRecords: { ...(prev.openRecords || {}), [TAB_KEY]: selectedId },
    }));
  }, [mode, examinationId, selectedId]);

  const selected = useMemo(() => plans.find((r) => r._id === selectedId) || null, [plans, selectedId]);

  const handleCreate = async () => {
    try {
      const linkTreatment =
        activeTreatment?._id && activeTreatment?.dietRequested ? activeTreatment._id : undefined;
      const res = await createPlan({
        token,
        patientId,
        ...(examinationId ? { examinationId } : {}),
        ...(linkTreatment ? { treatmentId: linkTreatment } : {}),
        title: "Beslenme planı",
        startDate: todayYmd(),
        days: [{ label: "Her gün", meals: [] }],
      }).unwrap();
      const plan = res?.data?.plan;
      await refresh();
      if (plan?._id) setSelectedId(plan._id);
      showSuccess(activePlan ? "Yeni plan oluşturuldu; önceki plan tamamlandı." : "Plan oluşturuldu.");
      onRecordChanged?.();
    } catch (err) {
      showError(err?.data?.message || "Plan oluşturulamadı.");
    }
  };

  const handleSaved = useCallback(
    (updated) => {
      if (!updated?._id) return;
      setPlans((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
      onRecordChanged?.();
    },
    [onRecordChanged]
  );

  const handleSetStatus = async (status) => {
    if (!selected) return;
    try {
      await setPlanStatus({ token, planId: selected._id, patientId, status, ...(examinationId ? { examinationId } : {}) }).unwrap();
      await refresh();
      showSuccess(status === "completed" ? "Plan tamamlandı." : "Plan aktif yapıldı.");
    } catch (err) {
      showError(err?.data?.message || "Durum değiştirilemedi.");
    }
  };

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Plan kaydedilemedi.");
    },
    [showError, terms]
  );

  const renderRow = (p) => {
    const st = PLAN_STATUS[p.status] || PLAN_STATUS.completed;
    const isActive = p.status === "active";
    return (
      <SelectableListRow
        key={p._id}
        active={p._id === selectedId}
        onClick={() => setSelectedId(p._id)}
        sx={LIST_ROW_SX}
        meta={`${formatDate(p.createdAt)}${p.days?.[0]?.totals?.calories ? ` · ${p.days[0].totals.calories} kcal` : ""}`}
        title={p.title || "Beslenme planı"}
        trailing={
          <Chip
            label={st.label}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              borderRadius: 999,
              bgcolor: isActive ? alpha(palette.primary, 0.08) : COLORS.slate100,
              color: isActive ? palette.primary : COLORS.slate600,
            }}
          />
        }
      />
    );
  };

  const showLoading = !loaded && isLoading;
  const selectedEditable = Boolean(canUpdate && selected && selected.status === "active");

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Beslenme Planı"
        count={plans.length}
        alert={localAlert}
        onAlertClose={hideLocal}
        action={
          canCreate && plans.length > 0 ? (
            <ActionButton variant="add" compact palette={palette} label="Yeni plan" loading={creating} onClick={handleCreate} />
          ) : null
        }
      >
        {showLoading ? (
          <WorkspaceSkeleton />
        ) : plans.length === 0 ? (
          <WorkspaceEmpty
            icon={<RestaurantIcon />}
            title="Henüz beslenme planı yok"
            lead="Öğün planı, hedefler ve besin listeleriyle"
            linkLabel="yeni plan oluşturun"
            readOnlyText={`Bu ${terms.patientLower} için beslenme planı bulunmuyor.`}
            onCreate={canCreate ? handleCreate : undefined}
            palette={palette}
          />
        ) : (
          <WorkspaceSplit
            list={
              <>
                {activePlan && (
                  <>
                    <ListGroupLabel first>Aktif Plan</ListGroupLabel>
                    {renderRow(activePlan)}
                  </>
                )}
                {pastPlans.length > 0 && (
                  <>
                    <ListGroupLabel first={!activePlan} count={pastPlans.length}>
                      Geçmiş Planlar
                    </ListGroupLabel>
                    <Stack divider={<Box sx={{ borderBottom: `1px solid ${COLORS.slate100}` }} />}>
                      {pastPlans.map(renderRow)}
                    </Stack>
                  </>
                )}
              </>
            }
            editor={
              !selected ? (
                <SelectPrompt
                  icon={<RestaurantIcon />}
                  title="Bir plan seçin"
                  description="Düzenlemek için aktif planı, görüntülemek için geçmiş bir planı seçin."
                />
              ) : (
                <>
                  {!selectedEditable && selected.status !== "active" && (
                    <ReadOnlyStrip>
                      {selected.status === "cancelled" ? "İptal edilmiş plan" : "Tamamlanmış plan"} — yalnızca görüntüleme
                    </ReadOnlyStrip>
                  )}
                  <DietEditor
                    key={selected._id}
                    record={selected}
                    patientId={patientId}
                    token={token}
                    examinationId={examinationId}
                    canEdit={selectedEditable}
                    weightSeries={weightSeries}
                    measurements={measurements}
                    patient={patient}
                    onSaved={handleSaved}
                    onError={handleEditorError}
                    onSetStatus={canUpdate ? handleSetStatus : undefined}
                    onPrint={(form) => setPrintPlan({ ...selected, ...form, measurements })}
                    registerAutoSave={registerAutoSave}
                    registerFlush={registerFlush}
                    onSaveStatusChange={onSaveStatusChange}
                  />
                </>
              )
            }
          />
        )}
      </WorkspaceBody>

      {printPlan && (
        <Suspense fallback={null}>
          <DietPlanPreview open={Boolean(printPlan)} onClose={() => setPrintPlan(null)} patient={patient} plan={printPlan} />
        </Suspense>
      )}
    </Box>
  );
}
